import React, {useMemo} from 'react';
import PageBreak from './PageBreak';
import TableOfContents from '../../../../../components/TableOfContents/TableOfContents';
import EndCover from './EndCover';
import StartCover from './StartCover';
import {buildDynamicTableOfContents} from '../generateTableOfContents';
import TableOfContentsSkeleton from './TableOfContentsSkeleton';
import Introduction from './chapters/Introduction';
import Summary from './chapters/Summary';
import County from './chapters/County';
import Caveats from './chapters/Caveats';
import {IAllSpecialtyCountyData, IAvgTimeDistData, IInitialData, IMaxTimeDistData, ISpecialtyGroupingsData} from '../types';

interface IGeneratedReportProps {
    clientName: string;
    specialtyGroupings: IInitialData<ISpecialtyGroupingsData>;
    maxTimeDistData: IInitialData<IMaxTimeDistData>;
    providerAvgTimeDistData: IInitialData<IAvgTimeDistData>;
    facilityAvgTimeDistData: IInitialData<IAvgTimeDistData>;
    allSpecialtyCountyData: IInitialData<IAllSpecialtyCountyData>;
    standardType: string;
    standardVersion: string;
    onMapsProgressChange?: (progress: {current: number, total: number}) => void;
}

const GeneratedReport = (props: IGeneratedReportProps) => {

    const {
        clientName,
        specialtyGroupings,
        maxTimeDistData,
        providerAvgTimeDistData,
        facilityAvgTimeDistData,
        allSpecialtyCountyData,
        standardType,
        standardVersion,
        onMapsProgressChange
    } = props;

    const tableOfContents = useMemo(() => {
        if (specialtyGroupings.loading) {
            return null;
        }
        return buildDynamicTableOfContents(
            standardType,
            standardVersion,
            specialtyGroupings.data
        );
    }, [specialtyGroupings.loading, specialtyGroupings.data, standardType, standardVersion]);

    const countyCount = useMemo(() => {
        if (!allSpecialtyCountyData.data?.data) return 0;

        const uniqueCounties = new Set<string>();
        Object.values(allSpecialtyCountyData.data.data).forEach(countyRows => {
            countyRows.forEach(row => {
                if (row.county_name) {
                    uniqueCounties.add(row.county_name);
                }
            });
        });

        return uniqueCounties.size;
    }, [allSpecialtyCountyData.data]);

    return (
        <div>
            <StartCover clientName={clientName} />
            {/* Show skeleton while specialty groupings are loading */}
            {!tableOfContents ? (
                <TableOfContentsSkeleton />
            ) : (
                <TableOfContents items={tableOfContents} />
            )}
            <Introduction
                specialtyGroupings={specialtyGroupings}
                maxTimeDistData={maxTimeDistData}
                standardType={standardType}
                standardVersion={standardVersion}
            />
            <PageBreak />
            <Summary
                clientName={clientName}
                providerAvgTimeDistData={providerAvgTimeDistData}
                facilityAvgTimeDistData={facilityAvgTimeDistData}
                standardType={standardType}
                countyCount={countyCount}
            />
            <PageBreak />
            <County
                clientName={clientName}
                specialtyGroupings={specialtyGroupings}
                allSpecialtyCountyData={allSpecialtyCountyData}
                standardType={standardType}
                onMapsProgressChange={onMapsProgressChange}
            />
            <PageBreak />
            <Caveats clientName={clientName} />
            <PageBreak />
            <EndCover />
        </div>
    );
};

export default GeneratedReport;
